import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { 
  Plus, Pencil, Trash2, Search, Building2, Loader2, KeyRound, Eye, EyeOff, UserPlus, Users
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Labs = () => {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editingLab, setEditingLab] = useState(null);
  const [passwordLab, setPasswordLab] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showFormPassword, setShowFormPassword] = useState(false);
  const [formData, setFormData] = useState({
    lab_name: '', contact_email: '', password: '',
    timezone: 'Europe/Paris', default_language: 'fr', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [showChangeMode, setShowChangeMode] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [usersLab, setUsersLab] = useState(null);
  const [labUsers, setLabUsers] = useState([]);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'technicien' });
  const [addingUser, setAddingUser] = useState(false);

  useEffect(() => { fetchLabs(); }, []);

  const fetchLabs = async () => {
    try {
      const response = await axios.get(`${API}/labs`);
      setLabs(response.data);
    } catch (error) {
      toast.error('Erreur de chargement des laboratoires');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (lab = null) => {
    if (lab) {
      setEditingLab(lab);
      setFormData({
        lab_name: lab.lab_name,
        contact_email: lab.contact_email || '',
        password: '',
        timezone: lab.timezone,
        default_language: lab.default_language,
        notes: lab.notes || ''
      });
    } else {
      setEditingLab(null);
      setFormData({ lab_name: '', contact_email: '', password: '', timezone: 'Europe/Paris', default_language: 'fr', notes: '' });
    }
    setShowFormPassword(false);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingLab) {
        const { password, ...updateData } = formData;
        await axios.put(`${API}/labs/${editingLab.id}`, updateData);
        toast.success('Laboratoire mis à jour');
      } else {
        await axios.post(`${API}/labs`, formData);
        toast.success('Laboratoire créé');
      }
      setDialogOpen(false);
      fetchLabs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (lab) => {
    if (!window.confirm(`Supprimer définitivement le laboratoire "${lab.lab_name}" ? Cette action est irréversible (sous-utilisateurs, canaux et messages associés seront aussi supprimés).`)) return;
    try {
      await axios.delete(`${API}/labs/${lab.id}`);
      toast.success('Laboratoire supprimé');
      fetchLabs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openPasswordDialog = async (lab) => {
    setPasswordLab(lab);
    setNewPassword('');
    setShowPassword(false);
    setShowChangeMode(false);
    setShowCurrentPwd(false);
    setCurrentPassword('');
    setPasswordDialogOpen(true);
    try {
      const r = await axios.get(`${API}/admin/credentials/lab/${lab.id}`);
      setCurrentPassword(r.data?.password || '');
    } catch {
      setCurrentPassword('');
    }
  };

  const handleSavePassword = async () => {
    if (!newPassword.trim()) { toast.error('Veuillez entrer un mot de passe'); return; }
    setSavingPassword(true);
    try {
      await axios.post(`${API}/labs/${passwordLab.id}/reset-password`, { password: newPassword });
      toast.success('Mot de passe modifié');
      setCurrentPassword(newPassword);
      setNewPassword('');
      setShowChangeMode(false);
    } catch {
      toast.error('Erreur lors de la modification');
    } finally {
      setSavingPassword(false);
    }
  };

  const filteredLabs = labs.filter(lab =>
    lab.lab_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lab.contact_email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openUsersDialog = async (lab) => {
    setUsersLab(lab);
    setUsersDialogOpen(true);
    setNewUser({ name: '', email: '', password: '', role: 'technicien' });
    try {
      const res = await axios.get(`${API}/labs/${lab.id}/users`);
      setLabUsers(res.data);
    } catch { setLabUsers([]); }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!usersLab) return;
    setAddingUser(true);
    try {
      await axios.post(`${API}/labs/${usersLab.id}/users`, newUser);
      toast.success('Utilisateur ajouté');
      setNewUser({ name: '', email: '', password: '', role: 'technicien' });
      const res = await axios.get(`${API}/labs/${usersLab.id}/users`);
      setLabUsers(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setAddingUser(false); }
  };

  const handleDeleteUser = async (userId) => {
    if (!usersLab || !window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await axios.delete(`${API}/labs/${usersLab.id}/users/${userId}`);
      toast.success('Utilisateur supprimé');
      setLabUsers(labUsers.filter(u => u.id !== userId));
    } catch { toast.error('Erreur'); }
  };

  return (
    <AdminLayout title="Laboratoires" subtitle="Gestion des laboratoires partenaires">
      <Card className="border-border" data-testid="labs-page">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Liste des laboratoires ({filteredLabs.length})
          </CardTitle>
          <Button onClick={() => handleOpenDialog()} data-testid="add-lab-button">
            <Plus className="h-4 w-4 mr-2" />Ajouter
          </Button>
        </CardHeader>
        <CardContent>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Rechercher un laboratoire..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-labs-input" />
          </div>

          {loading ? (
            <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : filteredLabs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">{searchTerm ? 'Aucun résultat' : 'Aucun laboratoire enregistré'}</div>
          ) : (
            <div className="rounded-lg border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Nom</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Langue</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLabs.map((lab) => (
                    <TableRow key={lab.id} data-testid={`lab-row-${lab.id}`}>
                      <TableCell className="font-medium">{lab.lab_name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{lab.contact_email || '-'}</TableCell>
                      <TableCell className="text-muted-foreground uppercase">{lab.default_language}</TableCell>
                      <TableCell>
                        <Badge variant={lab.active ? "default" : "secondary"} className={lab.active ? "status-active" : "status-inactive"}>
                          {lab.active ? 'Actif' : 'Inactif'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openUsersDialog(lab)} title="Utilisateurs labo" data-testid={`users-lab-${lab.id}`}>
                            <Users className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openPasswordDialog(lab)} title="Modifier le mot de passe" data-testid={`password-lab-${lab.id}`}>
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(lab)} data-testid={`edit-lab-${lab.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(lab)} className="text-destructive hover:text-destructive" title="Supprimer définitivement" data-testid={`delete-lab-${lab.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lab-dialog">
          <DialogHeader>
            <DialogTitle>{editingLab ? 'Modifier le laboratoire' : 'Nouveau laboratoire'}</DialogTitle>
            <DialogDescription>{editingLab ? 'Modifiez les informations' : 'Ajoutez un nouveau laboratoire partenaire'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="lab_name">Nom du laboratoire *</Label>
                <Input id="lab_name" value={formData.lab_name} onChange={(e) => setFormData({ ...formData, lab_name: e.target.value })} placeholder="Laboratoire XYZ" required data-testid="lab-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_email">Email du laboratoire *</Label>
                <Input id="contact_email" type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} placeholder="labo@exemple.com" required disabled={!!editingLab} data-testid="lab-email-input" />
              </div>
              {!editingLab && (
                <div className="space-y-2">
                  <Label htmlFor="lab_password">Mot de passe *</Label>
                  <div className="relative">
                    <Input id="lab_password" type={showFormPassword ? "text" : "password"} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Choisissez un mot de passe" required className="pr-10" data-testid="lab-password-input" />
                    <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3 hover:bg-transparent" onClick={() => setShowFormPassword(!showFormPassword)}>
                      {showFormPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fuseau horaire</Label>
                  <Select value={formData.timezone} onValueChange={(v) => setFormData({ ...formData, timezone: v })}>
                    <SelectTrigger data-testid="timezone-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Europe/Paris">Europe/Paris</SelectItem>
                      <SelectItem value="Europe/London">Europe/London</SelectItem>
                      <SelectItem value="America/New_York">America/New_York</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Langue</Label>
                  <Select value={formData.default_language} onValueChange={(v) => setFormData({ ...formData, default_language: v })}>
                    <SelectTrigger data-testid="language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="de">Deutsch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Notes internes..." rows={2} data-testid="lab-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={submitting} data-testid="save-lab-button">
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingLab ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lab-password-dialog">
          <DialogHeader>
            <DialogTitle>Mot de passe</DialogTitle>
            <DialogDescription>{passwordLab?.lab_name}</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Email de connexion</Label>
              <div className="p-3 bg-muted rounded-lg"><code className="text-sm font-mono">{passwordLab?.contact_email}</code></div>
            </div>
            <div className="space-y-2">
              <Label>Mot de passe actuel</Label>
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <code className="text-sm font-mono flex-1 break-all" data-testid="lab-current-password-display">
                  {currentPassword
                    ? (showCurrentPwd ? currentPassword : '•'.repeat(Math.max(currentPassword.length, 8)))
                    : <span className="text-muted-foreground italic">Aucun mot de passe lisible enregistré</span>}
                </code>
                {currentPassword && (
                  <>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowCurrentPwd(s => !s)} title={showCurrentPwd ? 'Masquer' : 'Afficher'} data-testid="lab-toggle-current-password">
                      {showCurrentPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => { navigator.clipboard?.writeText(currentPassword); toast.success('Copié'); }} title="Copier" data-testid="lab-copy-current-password">
                      <KeyRound className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
            {!showChangeMode ? (
              <Button variant="default" className="w-full" onClick={() => setShowChangeMode(true)} data-testid="lab-show-change-password">
                Changer le mot de passe
              </Button>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="new-lab-password">Nouveau mot de passe</Label>
                <div className="relative">
                  <Input id="new-lab-password" type={showPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Entrez le nouveau mot de passe" className="pr-10" data-testid="new-lab-password-input" />
                  <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3 hover:bg-transparent" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>Fermer</Button>
            {showChangeMode && (
              <Button onClick={handleSavePassword} disabled={savingPassword || !newPassword.trim()} data-testid="save-lab-password-btn">
                {savingPassword && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enregistrer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Lab Users Dialog */}
      <Dialog open={usersDialogOpen} onOpenChange={setUsersDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="lab-users-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5" />Utilisateurs — {usersLab?.lab_name}</DialogTitle>
            <DialogDescription>Chef de labo et technicien (max 2 utilisateurs)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Existing users */}
            {labUsers.length > 0 && (
              <div className="space-y-2">
                {labUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg" data-testid={`lab-user-${u.id}`}>
                    <div>
                      <p className="font-medium text-sm">{u.name}</p>
                      <p className="text-xs text-muted-foreground">{u.email} — <span className="capitalize">{u.role}</span></p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(u.id)} className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            )}
            {labUsers.length === 0 && <p className="text-sm text-muted-foreground text-center py-2">Aucun utilisateur ajouté. Le compte principal ({usersLab?.contact_email}) est actif.</p>}
            
            {/* Add user form */}
            {labUsers.length < 2 && (
              <form onSubmit={handleAddUser} className="border-t pt-4 space-y-3">
                <p className="text-sm font-medium flex items-center gap-2"><UserPlus className="h-4 w-4" />Ajouter un utilisateur</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Nom" value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} required data-testid="new-user-name" />
                  <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                    <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="chef">Chef</SelectItem><SelectItem value="technicien">Technicien</SelectItem></SelectContent>
                  </Select>
                </div>
                <Input type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required data-testid="new-user-email" />
                <Input type="text" placeholder="Mot de passe" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required data-testid="new-user-password" />
                <Button type="submit" disabled={addingUser} className="w-full" data-testid="add-user-btn">
                  {addingUser && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}<UserPlus className="h-4 w-4 mr-2" />Ajouter
                </Button>
              </form>
            )}
            {labUsers.length >= 2 && <p className="text-xs text-amber-600 text-center">Maximum 2 utilisateurs atteint</p>}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default Labs;
